import { useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Card, Alert, Button, Form, Badge, Spinner } from 'react-bootstrap'
import { useAppStore } from '../stores/appStore'
import LogViewer from '../components/LogViewer'
import { CultivateTask } from '../tasks/taskCultivate'

const DEFAULT_REQUEST_INTERVAL_MS = 300

const normalizeRequestInterval = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : DEFAULT_REQUEST_INTERVAL_MS
}

const CultivateGeneral = () => {
  const { accounts } = useAppStore()
  const [selectedAccount, setSelectedAccount] = useState('')
  const [generalList, setGeneralList] = useState([])
  const [selectedGeneral, setSelectedGeneral] = useState('')
  const [cultivateMode, setCultivateMode] = useState('1') // 1=银币, 2=元宝
  const [cultivateTimes, setCultivateTimes] = useState(10)
  const [cultivateTarget, setCultivateTarget] = useState('balance')
  const [requestInterval, setRequestInterval] = useState(DEFAULT_REQUEST_INTERVAL_MS)
  const [isExecuting, setIsExecuting] = useState(false)
  const [isLoadingGenerals, setIsLoadingGenerals] = useState(false)
  const [logs, setLogs] = useState([])
  const [executionProgress, setExecutionProgress] = useState(0)
  const [currentProgress, setCurrentProgress] = useState('')
  const isExecutingRef = useRef(false)
  const refreshGeneralListRef = useRef(null)
  const runtimeAccounts = accounts

  // 添加日志的方法
  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleString("zh-CN")
    const newLog = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp,
      message,
      type
    }
    
    setLogs(prevLogs => {
      const isDuplicate = prevLogs.some(log => 
        log.message === message && 
        log.type === type && 
        Math.abs(new Date(log.timestamp).getTime() - new Date(timestamp).getTime()) < 1000
      )
      
      if (isDuplicate) {
        return prevLogs
      }
      
      return [...prevLogs.slice(-99), newLog]
    })
  }

  // 清空日志的方法
  const clearLogs = () => {
    setLogs([])
  }

  // 创建独立的武将培养任务实例
  const [cultivateTaskInstance, setCultivateTaskInstance] = useState(null)

  // 初始化武将培养任务实例
  useEffect(() => {
    const task = new CultivateTask()
    // 设置appStore引用，使用动态获取的方式确保获取最新状态
    task.setAppStore({
      get accounts() { return useAppStore.getState().accounts },
      apiService: useAppStore.getState().apiService,
      getGlobalTaskConfig: useAppStore.getState().getGlobalTaskConfig,
      updateGlobalTaskConfig: useAppStore.getState().updateGlobalTaskConfig,
      updateUserInfo: useAppStore.getState().updateUserInfo,
      updateAccountAuth: useAppStore.getState().updateAccountAuth
    })
    task.setLogFunction(addLog)
    setCultivateTaskInstance(task)
  }, [])

  // 当accounts变化时，更新任务实例的appStore引用
  useEffect(() => {
    if (cultivateTaskInstance) {
      cultivateTaskInstance.setAppStore({
        get accounts() { return useAppStore.getState().accounts },
        apiService: useAppStore.getState().apiService,
        getGlobalTaskConfig: useAppStore.getState().getGlobalTaskConfig,
        updateGlobalTaskConfig: useAppStore.getState().updateGlobalTaskConfig,
        updateUserInfo: useAppStore.getState().updateUserInfo,
        updateAccountAuth: useAppStore.getState().updateAccountAuth
      })
    }
  }, [accounts, cultivateTaskInstance])

  // 刷新武将列表
  const refreshGeneralList = async () => {
    if (!selectedAccount) {
      addLog('请先选择账号', 'warning')
      return
    }

    if (!cultivateTaskInstance) {
      addLog('武将培养任务未初始化', 'error')
      return
    }

    setIsLoadingGenerals(true)
    try {
      // 确保任务实例有最新的appStore引用
      cultivateTaskInstance.setAppStore({
        get accounts() { return useAppStore.getState().accounts },
        apiService: useAppStore.getState().apiService,
        getGlobalTaskConfig: useAppStore.getState().getGlobalTaskConfig,
        updateGlobalTaskConfig: useAppStore.getState().updateGlobalTaskConfig,
        updateUserInfo: useAppStore.getState().updateUserInfo,
        updateAccountAuth: useAppStore.getState().updateAccountAuth
      })

      // 先检查登录状态，确保token有效
      await cultivateTaskInstance.checkLoginStatus(selectedAccount)
      
      // 重新设置appStore引用以获取更新后的token
      cultivateTaskInstance.setAppStore({
        get accounts() { return useAppStore.getState().accounts },
        apiService: useAppStore.getState().apiService,
        getGlobalTaskConfig: useAppStore.getState().getGlobalTaskConfig,
        updateGlobalTaskConfig: useAppStore.getState().updateGlobalTaskConfig,
        updateUserInfo: useAppStore.getState().updateUserInfo,
        updateAccountAuth: useAppStore.getState().updateAccountAuth
      })
      
      // 获取武将列表
      const generals = await cultivateTaskInstance.getGeneralList(selectedAccount)
      setGeneralList(generals)
      
      if (generals.length > 0) {
        addLog(`成功获取 ${generals.length} 个武将信息`, 'success')
        // 如果当前没有选中武将，自动选择第一个
        if (!selectedGeneral && generals.length > 0) {
          setSelectedGeneral(generals[0].id.toString())
        }
      } else {
        addLog('未获取到武将信息，请检查账号状态', 'warning')
      }
    } catch (error) {
      addLog(`获取武将列表失败: ${error.message}`, 'error')
      setGeneralList([])
    } finally {
      setIsLoadingGenerals(false)
    }
  }

  // 只在账号/任务实例改变时自动刷新，不因日志或武将选择变化重复请求。
  refreshGeneralListRef.current = refreshGeneralList
  // 当选择账号改变时，清空武将列表
  useEffect(() => {
    setGeneralList([])
    setSelectedGeneral('')
    if (selectedAccount && cultivateTaskInstance) {
      // 自动刷新武将列表
      refreshGeneralListRef.current()
    }
  }, [selectedAccount, cultivateTaskInstance])

  // 执行武将培养
  const executeCultivate = async () => {
    if (!selectedAccount) {
      addLog('请选择账号', 'error')
      return
    }

    if (!selectedGeneral) {
      addLog('请选择武将', 'error')
      return
    }

    if (!cultivateTimes || cultivateTimes < 1) {
      addLog('请输入有效的培养次数', 'error')
      return
    }

    if (!cultivateTaskInstance) {
      addLog('武将培养任务未初始化', 'error')
      return
    }

    setIsExecuting(true)
    isExecutingRef.current = true
    setExecutionProgress(0)
    setCurrentProgress('')

    try {
      // 更新任务配置
      const config = {
        generalId: selectedGeneral,
        mode: cultivateMode,
        times: parseInt(cultivateTimes),
        target: cultivateTarget,
        requestInterval: normalizeRequestInterval(requestInterval)
      }

      cultivateTaskInstance.updateConfig(selectedAccount, config)
      useAppStore.getState().apiService.requestInterval = config.requestInterval

      addLog(`开始培养武将，配置: ${JSON.stringify(config)}`, 'info')

      // 获取选中的武将信息
      const selectedGeneralInfo = generalList.find(g => g.id.toString() === selectedGeneral)
      if (selectedGeneralInfo) {
        addLog(`选中武将: ${selectedGeneralInfo.name} (武${selectedGeneralInfo.wuliup} 智${selectedGeneralInfo.zhiliup} 体${selectedGeneralInfo.tiliup})`, 'info')
      }

      // 确保任务实例有最新的appStore引用
      cultivateTaskInstance.setAppStore({
        get accounts() { return useAppStore.getState().accounts },
        apiService: useAppStore.getState().apiService,
        getGlobalTaskConfig: useAppStore.getState().getGlobalTaskConfig,
        updateGlobalTaskConfig: useAppStore.getState().updateGlobalTaskConfig,
        updateUserInfo: useAppStore.getState().updateUserInfo,
        updateAccountAuth: useAppStore.getState().updateAccountAuth
      })

      // 先检查登录状态
      await cultivateTaskInstance.checkLoginStatus(selectedAccount)

      // 重新设置appStore引用以获取更新后的token
      cultivateTaskInstance.setAppStore({
        get accounts() { return useAppStore.getState().accounts },
        apiService: useAppStore.getState().apiService,
        getGlobalTaskConfig: useAppStore.getState().getGlobalTaskConfig,
        updateGlobalTaskConfig: useAppStore.getState().updateGlobalTaskConfig,
        updateUserInfo: useAppStore.getState().updateUserInfo,
        updateAccountAuth: useAppStore.getState().updateAccountAuth
      })

      // 执行培养任务
      let successCount = 0
      const totalTimes = parseInt(cultivateTimes)

      for (let i = 1; i <= totalTimes; i++) {
        // 检查是否被停止
        if (!isExecutingRef.current) {
          addLog('培养已被用户停止', 'warning')
          break
        }

        setCurrentProgress(`第 ${i}/${totalTimes} 次培养`)
        setExecutionProgress((i / totalTimes) * 100)

        try {
          addLog(`[${selectedAccount}] 第${i}/${totalTimes}次培养...`, 'info')
          
          const result = await cultivateTaskInstance.cultivateOnce(
            selectedAccount,
            selectedGeneral,
            cultivateMode,
            cultivateTarget
          )

          if (result && result.saved) {
            successCount++
          }
        } catch (error) {
          addLog(`第 ${i} 次培养失败: ${error.message}`, 'error')
        }
      }

      const successRate = totalTimes > 0 ? ((successCount / totalTimes) * 100).toFixed(1) : 0
      addLog(`培养完成！成功 ${successCount}/${totalTimes} 次，成功率 ${successRate}%`, 'success')

      // 刷新武将列表以显示最新属性
      await refreshGeneralList()

    } catch (error) {
      addLog(`培养执行失败: ${error.message}`, 'error')
    } finally {
      setIsExecuting(false)
      isExecutingRef.current = false
      setExecutionProgress(0)
      setCurrentProgress('')
    }
  }

  // 停止培养
  const stopCultivate = () => {
    setIsExecuting(false)
    isExecutingRef.current = false
    addLog('用户手动停止培养', 'warning')
  }

  // 检查是否有账号
  const hasAccounts = Object.keys(runtimeAccounts).length > 0

  if (!hasAccounts) {
    return (
      <Container fluid>
        <Row className="mb-4">
          <Col xs={12}>
            <Alert variant="warning" className="text-center">
              <h5>请先添加账号</h5>
              <p>您需要在账号管理页面添加账号后才能使用武将培养功能。</p>
            </Alert>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container fluid>
      {/* 页面标题 */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="card-title text-primary mb-1">⚔️ 武将培养</h4>
                  <p className="card-text text-muted mb-0">选择账号 → 选择武将 → 配置培养参数 → 开始培养</p>
                </div>
                <div className="d-flex gap-2">
                  <Badge bg="info">{selectedAccount ? '已选择账号' : '未选择账号'}</Badge>
                  <Badge bg="success">{selectedGeneral ? '已选择武将' : '未选择武将'}</Badge>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* 左侧：账号和武将选择 */}
        <Col lg={4}>
          {/* 账号选择 */}
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">选择账号</h6>
            </Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                >
                  <option value="">请选择账号</option>
                  {Object.keys(runtimeAccounts).map(accountKey => {
                    const account = runtimeAccounts[accountKey]
                    return (
                      <option key={accountKey} value={accountKey}>
                        {account.username} ({account.server})
                        {account.userInfo?.nickname && ` - ${account.userInfo.nickname}`}
                      </option>
                    )
                  })}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>

          {/* 武将选择 */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">选择武将</h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={refreshGeneralList}
                disabled={!selectedAccount || isLoadingGenerals}
              >
                {isLoadingGenerals ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    刷新中
                  </>
                ) : (
                  '刷新列表'
                )}
              </Button>
            </Card.Header>
            <Card.Body>
              {!selectedAccount ? (
                <div className="text-center text-muted py-3">
                  <p>请先选择账号</p>
                </div>
              ) : generalList.length === 0 ? (
                <div className="text-center text-muted py-3">
                  <p>暂无武将数据</p>
                  <small>请点击&ldquo;刷新列表&rdquo;获取武将信息</small>
                </div>
              ) : (
                <Form.Group>
                  <Form.Select
                    value={selectedGeneral}
                    onChange={(e) => setSelectedGeneral(e.target.value)}
                  >
                    <option value="">请选择武将</option>
                    {generalList.map(general => (
                      <option key={general.id} value={general.id.toString()}>
                        {general.name} (武{general.wuliup} 智{general.zhiliup} 体{general.tiliup})
                      </option>
                    ))}
                  </Form.Select>
                  {selectedGeneral && (
                    <Form.Text className="text-muted">
                      {(() => {
                        const general = generalList.find(g => g.id.toString() === selectedGeneral)
                        return general ? `当前属性：武力${general.wuliup} 智力${general.zhiliup} 体力${general.tiliup}` : ''
                      })()}
                    </Form.Text>
                  )}
                </Form.Group>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* 右侧：培养配置和执行控制 */}
        <Col lg={8}>
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">培养配置</h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>培养方式</Form.Label>
                    <Form.Select
                      value={cultivateMode}
                      onChange={(e) => setCultivateMode(e.target.value)}
                      disabled={isExecuting}
                    >
                      <option value="1">银币培养</option>
                      <option value="2">元宝培养</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>培养次数</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="999"
                      value={cultivateTimes}
                      onChange={(e) => setCultivateTimes(e.target.value)}
                      disabled={isExecuting}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>培养目标</Form.Label>
                <Form.Select
                  value={cultivateTarget}
                  onChange={(e) => setCultivateTarget(e.target.value)}
                  disabled={isExecuting}
                >
                  <option value="balance">平衡发展</option>
                  <option value="wuli">主练武力</option>
                  <option value="zhili">主练智力</option>
                  <option value="tili">主练体力</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  平衡发展：总属性提升优先；单项发展：指定属性提升优先
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>请求间隔（毫秒）</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="50"
                  value={requestInterval}
                  onChange={(e) => setRequestInterval(e.target.value)}
                  onBlur={(e) => setRequestInterval(normalizeRequestInterval(e.target.value))}
                  disabled={isExecuting}
                />
                <Form.Text className="text-muted">
                  控制武将培养接口之间的最小间隔，默认 300ms。
                </Form.Text>
              </Form.Group>

              {/* 执行控制 */}
              <div className="d-grid gap-2">
                {!isExecuting ? (
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={executeCultivate}
                    disabled={!selectedAccount || !selectedGeneral}
                  >
                    开始培养
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={stopCultivate}
                  >
                    停止培养
                  </Button>
                )}
              </div>

              {/* 执行进度 */}
              {isExecuting && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between mb-1">
                    <small>培养进度</small>
                    <small>{Math.round(executionProgress)}%</small>
                  </div>
                  <div className="progress mb-2">
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${executionProgress}%` }}
                    ></div>
                  </div>
                  {currentProgress && (
                    <small className="text-muted">{currentProgress}</small>
                  )}
                </div>
              )}

              {/* 执行前检查 */}
              {(!selectedAccount || !selectedGeneral) && (
                <Alert variant="warning" className="mt-3">
                  {!selectedAccount && '请选择账号'}
                  {!selectedGeneral && '请选择武将'}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 日志区域 */}
      <LogViewer logs={logs} onClearLogs={clearLogs} />
    </Container>
  )
}

export default CultivateGeneral
